import { assign, fromPromise, setup } from 'xstate';
import { getPendingRequestConflict } from '~/features/utils/pendingRequestConflict';
import {
  getColleagueInvitations,
  resendColleagueInvitation,
  revokeColleagueInvitation,
  sendColleagueInvitation,
} from './api';
import { EmployerUserInvitationSummary } from './types';

type Alert = {
  text: string;
  type: 'success' | 'error';
};

type Context = {
  employerId: number;
  invitations: EmployerUserInvitationSummary[];
  loading: boolean;
  modalOpen: boolean;
  inviteEmail: string;
  inviteValidationError: string;
  // [MCV-1814] The invitation a duplicate send collided with, while the modal
  // asks whether to resend it.
  pendingInvitationId: number | null;
  alert: Alert | null;
};

type Events =
  | { type: 'ON_LOAD'; employerId: number }
  | { type: 'ON_OPEN_MODAL' }
  | { type: 'ON_CLOSE_MODAL' }
  | { type: 'ON_SET_INVITE_EMAIL'; email: string }
  | { type: 'ON_SEND_INVITATION' }
  | { type: 'ON_RESEND_PENDING' }
  | { type: 'ON_CANCEL_PENDING' }
  | { type: 'ON_RESEND_INVITATION'; invitationId: number }
  | { type: 'ON_REVOKE_INVITATION'; invitationId: number }
  | { type: 'ON_CLEAR_ALERT' };

type InputArgs = { input: { context: Context; event: Events } };

const isValidInviteEmail = (email: string): boolean =>
  email.length > 0 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

const normalizeInvitationStatus = (
  invitation: EmployerUserInvitationSummary
): EmployerUserInvitationSummary => {
  const expiresAt = Date.parse(invitation.expiresAt);
  const isExpiredResendableInvitation =
    (invitation.status === 'PENDING' || invitation.status === 'EMAIL_FAILED') &&
    !Number.isNaN(expiresAt) &&
    expiresAt <= Date.now();

  return isExpiredResendableInvitation ? { ...invitation, status: 'EXPIRED' } : invitation;
};

const loadInvitations = async ({ input }: InputArgs) => {
  const invitations = await getColleagueInvitations(input.context.employerId);
  return invitations.map(normalizeInvitationStatus);
};

const sendInvite = async ({ input }: InputArgs) => {
  return sendColleagueInvitation(input.context.employerId, input.context.inviteEmail.trim());
};

const revokeInvite = async ({ input }: InputArgs) => {
  const event = input.event as Extract<Events, { type: 'ON_REVOKE_INVITATION' }>;
  return revokeColleagueInvitation(input.context.employerId, event.invitationId);
};

const resendInvite = async ({ input }: InputArgs) => {
  const event = input.event as Extract<Events, { type: 'ON_RESEND_INVITATION' }>;
  return resendColleagueInvitation(input.context.employerId, event.invitationId);
};

const resendPending = async ({ input }: InputArgs) => {
  const { employerId, pendingInvitationId } = input.context;
  if (pendingInvitationId == null) throw new Error('No pending invitation to resend');
  return resendColleagueInvitation(employerId, pendingInvitationId);
};

export const colleagueInviteTeamMachine = setup({
  types: {
    context: {} as Context,
    events: {} as Events,
  },
  actors: {
    loadInvitations: fromPromise(loadInvitations),
    sendInvite: fromPromise(sendInvite),
    resendInvite: fromPromise(resendInvite),
    resendPending: fromPromise(resendPending),
    revokeInvite: fromPromise(revokeInvite),
  },
  guards: {
    canSendInvite: ({ context }) =>
      isValidInviteEmail(context.inviteEmail.trim()) && context.employerId > 0,
  },
  actions: {
    openModal: assign({
      modalOpen: true,
      inviteEmail: '',
      inviteValidationError: '',
      pendingInvitationId: null,
    }),
    closeModal: assign({
      modalOpen: false,
      inviteEmail: '',
      inviteValidationError: '',
      pendingInvitationId: null,
    }),
    clearPending: assign({ pendingInvitationId: null }),
    setLoading: assign({ loading: true }),
    clearLoading: assign({ loading: false }),
  },
}).createMachine({
  id: 'colleagueInviteTeam',
  context: {
    employerId: -1,
    invitations: [],
    loading: false,
    modalOpen: false,
    inviteEmail: '',
    inviteValidationError: '',
    pendingInvitationId: null,
    alert: null,
  },
  initial: '$_IDLE',
  on: {
    ON_LOAD: {
      target: '.$_LOADING',
      actions: assign({
        employerId: ({ event }) => event.employerId,
      }),
    },
    ON_CLOSE_MODAL: { actions: 'closeModal' },
    ON_SET_INVITE_EMAIL: {
      actions: assign({
        inviteEmail: ({ event }) => event.email,
        inviteValidationError: '',
      }),
    },
    ON_CLEAR_ALERT: { actions: assign({ alert: null }) },
  },
  states: {
    $_IDLE: {},
    $_VIEW: {
      on: {
        ON_OPEN_MODAL: { actions: 'openModal' },
        ON_SEND_INVITATION: [
          {
            guard: 'canSendInvite',
            target: '$_SENDING_INVITE',
          },
          {
            actions: assign({
              inviteValidationError: 'Please enter a valid email address',
            }),
          },
        ],
        ON_RESEND_INVITATION: { target: '$_RESENDING_INVITE' },
        ON_REVOKE_INVITATION: { target: '$_REVOKING_INVITE' },
      },
    },
    $_LOADING: {
      entry: 'setLoading',
      invoke: {
        src: 'loadInvitations',
        input: ({ context, event }) => ({ context, event }),
        onDone: {
          target: '$_VIEW',
          actions: assign({
            invitations: ({ event }) => event.output,
            loading: false,
          }),
        },
        onError: {
          target: '$_VIEW',
          actions: assign({
            loading: false,
            alert: {
              text: 'Unable to load colleague invitations',
              type: 'error',
            },
          }),
        },
      },
    },
    $_SENDING_INVITE: {
      invoke: {
        src: 'sendInvite',
        input: ({ context, event }) => ({ context, event }),
        onDone: {
          target: '$_LOADING',
          actions: assign({
            modalOpen: false,
            inviteEmail: '',
            inviteValidationError: '',
            alert: ({ context, event }) => ({
              text: context.invitations.some((invitation) => invitation.id === event.output.id)
                ? 'Invitation resent'
                : 'Invitation sent successfully',
              type: 'success' as const,
            }),
          }),
        },
        onError: [
          {
            // [MCV-1814] A pending invitation to this email already exists:
            // nothing was created, reissued or emailed. Keep the modal open and
            // ask whether to resend that one.
            guard: ({ event }) => getPendingRequestConflict(event.error) !== null,
            target: '$_PENDING_EXISTS',
            actions: assign({
              pendingInvitationId: ({ event }) =>
                getPendingRequestConflict(event.error)?.existingId ?? null,
            }),
          },
          {
            target: '$_VIEW',
            actions: assign({
              // Surface the server detail so a 502 (SES rejected the send) reads
              // differently from a 409 (already a member).
              // Without it every failure collapsed into one opaque string.
              //
              // Only when it is actually a string: Litestar returns `detail` as an
              // array of error objects for validation failures, and an array is
              // truthy, so a bare `||` would put "[object Object]" in front of the
              // user. Anything non-string falls back to the generic message.
              alert: ({ event }) => {
                const detail = (event.error as { response?: { data?: { detail?: unknown } } })
                  ?.response?.data?.detail;
                return {
                  text: typeof detail === 'string' ? detail : 'Unable to send invitation',
                  type: 'error' as const,
                };
              },
            }),
          },
        ],
      },
    },
    // [MCV-1814] The modal shows "A pending request already exists for {email}.
    // Resend it?". Resend uses the same endpoint as the row action; Cancel
    // returns to the form with the typed email intact.
    $_PENDING_EXISTS: {
      on: {
        ON_RESEND_PENDING: { target: '$_RESENDING_PENDING' },
        ON_CANCEL_PENDING: { target: '$_VIEW', actions: 'clearPending' },
        ON_CLOSE_MODAL: { target: '$_VIEW', actions: 'closeModal' },
      },
    },
    $_RESENDING_PENDING: {
      invoke: {
        src: 'resendPending',
        input: ({ context, event }) => ({ context, event }),
        onDone: {
          target: '$_LOADING',
          actions: assign({
            modalOpen: false,
            inviteEmail: '',
            inviteValidationError: '',
            pendingInvitationId: null,
            alert: { text: 'Request resent', type: 'success' },
          }),
        },
        onError: {
          target: '$_LOADING',
          actions: assign({
            modalOpen: false,
            inviteEmail: '',
            inviteValidationError: '',
            pendingInvitationId: null,
            alert: ({ event }) => {
              const detail = (event.error as { response?: { data?: { detail?: unknown } } })
                ?.response?.data?.detail;
              return {
                text: typeof detail === 'string' ? detail : 'Unable to resend invitation',
                type: 'error' as const,
              };
            },
          }),
        },
      },
    },
    $_RESENDING_INVITE: {
      invoke: {
        src: 'resendInvite',
        input: ({ context, event }) => ({ context, event }),
        onDone: {
          target: '$_LOADING',
          actions: assign({
            alert: {
              text: 'Invitation resent',
              type: 'success',
            },
          }),
        },
        onError: {
          target: '$_LOADING',
          actions: assign({
            alert: ({ event }) => {
              const detail = (event.error as { response?: { data?: { detail?: unknown } } })
                ?.response?.data?.detail;
              return {
                text: typeof detail === 'string' ? detail : 'Unable to resend invitation',
                type: 'error' as const,
              };
            },
          }),
        },
      },
    },
    $_REVOKING_INVITE: {
      invoke: {
        src: 'revokeInvite',
        input: ({ context, event }) => ({ context, event }),
        onDone: {
          target: '$_LOADING',
          actions: assign({
            alert: {
              text: 'Invitation revoked',
              type: 'success',
            },
          }),
        },
        onError: {
          target: '$_VIEW',
          actions: assign({
            alert: {
              text: 'Unable to revoke invitation',
              type: 'error',
            },
          }),
        },
      },
    },
  },
});
